import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
  useCallback,
} from 'react'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel } from '../ImporterViewModel'
import { Tabs } from 'components/FbUI/Tabs'

export function ImportWizardReviewPendingStep({
  show,
  data,
}: {
  show: boolean
  data: CSVRow[]
}) {
  const viewModel = useImporterViewModel()
  const [cursor, setCursor] = useState(0)
  const [selectedChain, setSelectedChain] = useState<
    Record<string, ChainSearchResult>
  >({})

  const [selectedDoor, setSelectedDoor] = useState<
    Record<string, DoorSearchResult>
  >({})

  function handleNext() {
    if (cursor + 1 >= data.length) {
      return
    }
    setCursor(cursor + 1)
  }

  function handlePrev() {
    if (cursor - 1 < 0) {
      return
    }
    setCursor(cursor - 1)
  }

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      if (e.key === 'j') {
        e.preventDefault()
        if (cursor > 0) {
          setCursor(cursor - 1)
        }
      } else if (e.key === 'k') {
        e.preventDefault()
        if (cursor < data.length - 1) {
          setCursor(cursor + 1)
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [cursor, data.length])

  // Pre-fill selections
  useEffect(() => {
    if (Object.keys(selectedChain).length > 0) {
      return
    }

    type InitialSelections = {
      chains: Record<string, ChainSearchResult>
      doors: Record<string, DoorSearchResult>
    }
    const initialSelections = viewModel.importData?.rows.reduce(
      (acc: InitialSelections, row: CSVRow) => {
        const newAcc = structuredClone(acc)
        const hash = row[InternalColumns.COMPANY_HASH]
        const matchedChain = row[InternalColumns.MATCHED_CHAIN]
        const matchedDoor = row[InternalColumns.MATCHED_DOOR]
        if (matchedChain) {
          newAcc.chains[hash] = matchedChain
        }
        if (matchedDoor) {
          newAcc.doors[hash] = matchedDoor
        }
        return newAcc
      },
      { chains: {}, doors: {} } as InitialSelections
    )

    if (initialSelections) {
      setSelectedChain(initialSelections.chains)
      setSelectedDoor(initialSelections.doors)
    }
  }, [viewModel.importData])

  if (!show) {
    return null
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No pending records to review
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Progress header */}
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-2">
          <div className="text-lg font-bold text-gray-700">
            {cursor + 1}
            <span className="text-gray-400"> / {data.length}</span>
          </div>
          <div className="h-1.5 w-32 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-fb-dark-green transition-all duration-300"
              style={{ width: `${((cursor + 1) / data.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <FbButton
              type="button"
              onClick={() => {
                const hash = data[cursor][InternalColumns.COMPANY_HASH]
                setSelectedChain((prev) => {
                  const newState = { ...prev }
                  delete newState[hash]
                  return newState
                })
                setSelectedDoor((prev) => {
                  const newState = { ...prev }
                  delete newState[hash]
                  return newState
                })
                viewModel.updateMatchedChainFromCompanyHash(hash, null, false)
                viewModel.updateMatchedDoorFromCompanyHash(hash, null, false)
              }}
              variant="secondary"
            >
              Clear
            </FbButton>
            <FbButton
              type="button"
              onClick={handlePrev}
              disabled={cursor === 0}
            >
              <Kbd>J</Kbd> ← Prev
            </FbButton>
            <FbButton
              type="button"
              onClick={handleNext}
              disabled={cursor === data.length - 1}
            >
              <Kbd>K</Kbd> Next →
            </FbButton>
          </div>
        </div>
      </div>

      {/* Review card */}
      <div className="flex-1 overflow-auto">
        <ReviewCard
          selectedChain={selectedChain}
          selectedDoor={selectedDoor}
          setSelectedChain={setSelectedChain}
          setSelectedDoor={setSelectedDoor}
          csvRow={data[cursor]}
          headers={viewModel.wizardHeaders}
        />
      </div>
    </div>
  )
}

function ReviewCard({
  csvRow,
  headers,
  selectedChain,
  selectedDoor,
  setSelectedChain,
  setSelectedDoor,
}: {
  csvRow: CSVRow
  headers: Record<string, string>
  selectedChain: Record<string, ChainSearchResult>
  selectedDoor: Record<string, DoorSearchResult>
  setSelectedChain: Dispatch<SetStateAction<Record<string, ChainSearchResult>>>
  setSelectedDoor: Dispatch<SetStateAction<Record<string, DoorSearchResult>>>
}) {
  const viewModel = useImporterViewModel()
  type ReviewTab = 'chain' | 'door'
  const [tab, setTab] = useState<ReviewTab>('chain')

  const currentSelectedChain =
    selectedChain[csvRow[InternalColumns.COMPANY_HASH]] ??
    csvRow[InternalColumns.MATCHED_CHAIN]
  const currentSelectedDoor =
    selectedDoor[csvRow[InternalColumns.COMPANY_HASH]] ??
    csvRow[InternalColumns.MATCHED_DOOR]

  // Sort and limit to top 5 suggestions by score
  const topChains = useMemo(() => {
    const sorted = [...(csvRow.__internal__suggested_chains ?? [])]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
    // Add hidden placeholders to always have 5 items
    const placeholders = Array(5 - sorted.length).fill(null)
    return [...sorted, ...placeholders]
  }, [csvRow.__internal__suggested_chains])

  const topDoors = useMemo(() => {
    const sorted = [...(csvRow.__internal__suggested_doors ?? [])]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
    // Add hidden placeholders to always have 5 items
    const placeholders = Array(5 - sorted.length).fill(null)
    return [...sorted, ...placeholders]
  }, [csvRow.__internal__suggested_doors])

  const onSelectChain = useCallback(
    (value: ChainSearchResult) => {
      // If already selected, deselect it
      if (currentSelectedChain?.item_id === value.item_id) {
        setSelectedChain((prev) => {
          const newState = { ...prev }
          delete newState[csvRow[InternalColumns.COMPANY_HASH]]
          return newState
        })
        viewModel.updateMatchedChainFromCompanyHash(
          csvRow[InternalColumns.COMPANY_HASH],
          null,
          false
        )
        return
      }

      setSelectedChain((prev) => ({
        ...prev,
        [csvRow[InternalColumns.COMPANY_HASH]]: value,
      }))

      const chain = csvRow.__internal__suggested_chains?.find(
        (c) => c.item_id === value.item_id
      )

      if (chain) {
        viewModel.updateMatchedChainFromCompanyHash(
          csvRow[InternalColumns.COMPANY_HASH],
          chain,
          false
        )
      }
    },
    [
      csvRow[InternalColumns.COMPANY_HASH],
      csvRow.__internal__suggested_chains,
      viewModel,
      setSelectedChain,
      currentSelectedChain,
    ]
  )

  const onSelectDoor = useCallback(
    (value: DoorSearchResult) => {
      // If already selected, deselect it
      if (currentSelectedDoor?.item_id === value.item_id) {
        setSelectedDoor((prev) => {
          const newState = { ...prev }
          delete newState[csvRow[InternalColumns.COMPANY_HASH]]
          return newState
        })
        viewModel.updateMatchedDoorFromCompanyHash(
          csvRow[InternalColumns.COMPANY_HASH],
          null,
          false
        )
        viewModel.updateMatchedChainFromCompanyHash(
          csvRow[InternalColumns.COMPANY_HASH],
          null,
          false
        )
        return
      }

      setSelectedDoor((prev) => ({
        ...prev,
        [csvRow[InternalColumns.COMPANY_HASH]]: value,
      }))

      const door = csvRow.__internal__suggested_doors?.find(
        (d) => d.item_id === value.item_id
      )

      if (door) {
        const chain =
          csvRow.__internal__suggested_chains?.find(
            (c) => c.item_id === door?.chain_id
          ) ?? null

        viewModel.updateMatchedDoorFromCompanyHash(
          csvRow[InternalColumns.COMPANY_HASH],
          door,
          false
        )
        viewModel.updateMatchedChainFromCompanyHash(
          csvRow[InternalColumns.COMPANY_HASH],
          chain,
          false
        )
      }
    },
    [
      csvRow[InternalColumns.COMPANY_HASH],
      csvRow.__internal__suggested_doors,
      csvRow.__internal__suggested_chains,
      viewModel,
      setSelectedDoor,
      currentSelectedDoor,
    ]
  )

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      // Tab switching
      if (e.key === 'u') {
        setTab('chain')
        e.preventDefault()
        return
      }
      if (e.key === 'i') {
        setTab('door')
        e.preventDefault()
        return
      }

      // Selection shortcuts (1-5)
      const num = parseInt(e.key)
      if (num >= 1 && num <= 5) {
        e.preventDefault()
        const index = num - 1

        if (tab === 'chain') {
          const chain = topChains[index]
          if (chain) {
            onSelectChain(chain)
          }
        } else {
          const door = topDoors[index]
          if (door) {
            onSelectDoor(door)
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [tab, topChains, topDoors, onSelectChain, onSelectDoor])

  const inputData = {
    name: csvRow[headers.name] || '-',
    domain: csvRow[headers.domain] || '-',
    address:
      csvRow[headers.full_address] ||
      `${csvRow[headers.street] || ''}, ${csvRow[headers.city] || ''}, ${csvRow[headers.state] || ''} ${csvRow[headers.zip] || ''}`.trim() ||
      '-',
  }

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Comparison Summary */}
      <ComparisonSummary
        inputData={inputData}
        selectedChain={currentSelectedChain}
        selectedDoor={currentSelectedDoor}
        isChainManualMatch={csvRow[InternalColumns.IS_CHAIN_MANUAL_MATCH]}
        isDoorManualMatch={csvRow[InternalColumns.IS_DOOR_MANUAL_MATCH]}
      />

      {/* Suggestions Section */}
      <div className="flex-1 flex flex-col">
        <Tabs
          tabs={[
            {
              title: (
                <div className="flex flex-row items-center">
                  <Kbd>U</Kbd> &nbsp; Chains (
                  {Math.min(
                    csvRow.__internal__suggested_chains?.length || 0,
                    5
                  )}
                  )
                </div>
              ),
              key: 'chain',
            },
            {
              title: (
                <div className="flex flex-row items-center">
                  <Kbd>I</Kbd> &nbsp; Doors (
                  {Math.min(csvRow.__internal__suggested_doors?.length || 0, 5)}
                  )
                </div>
              ),
              key: 'door',
            },
          ]}
          selectedTab={tab}
          setSelectedTab={setTab}
        />

        <div className="mt-2 flex-1">
          {tab === 'chain' && (
            <div>
              {/* Header */}
              <div className="flex items-center gap-2 px-2 py-1 mb-1 text-sm font-medium text-gray-600 border-b">
                <div className="flex-shrink-0 w-6"></div>
                <div className="flex-shrink-0 w-4"></div>
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>Name</div>
                  <div>Domain</div>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-1.5">
                {topChains.map((chain, index) =>
                  chain ? (
                    <SuggestionCard
                      key={chain.item_id}
                      data={chain}
                      isSelected={
                        chain.item_id === currentSelectedChain?.item_id
                      }
                      onSelect={() => onSelectChain(chain)}
                      number={index + 1}
                    />
                  ) : (
                    <div
                      key={`placeholder-chain-${index}`}
                      className="invisible"
                    >
                      <SuggestionCard
                        data={{
                          item_id: '',
                          name: '-',
                          domain: '-',
                          count: 0,
                          type: '',
                          score: 0,
                        }}
                        isSelected={false}
                        onSelect={() => {}}
                        number={index + 1}
                      />
                    </div>
                  )
                )}
                {/* Manual Search Button */}
                <ManualSearchCard
                  onClick={() => {
                    const hash = csvRow[InternalColumns.COMPANY_HASH]
                    setSelectedChain((prev) => {
                      const newState = { ...prev }
                      delete newState[hash]
                      return newState
                    })
                    setSelectedDoor((prev) => {
                      const newState = { ...prev }
                      delete newState[hash]
                      return newState
                    })
                    viewModel.setSelectedRowId(csvRow[InternalColumns.ROW_ID])
                  }}
                />
              </div>
            </div>
          )}
          {tab === 'door' && (
            <div>
              {/* Header */}
              <div className="flex items-center gap-2 px-2 py-1 mb-1 text-sm font-medium text-gray-600 border-b">
                <div className="flex-shrink-0 w-6"></div>
                <div className="flex-shrink-0 w-4"></div>
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div>Name</div>
                  <div>Domain</div>
                  <div>Address</div>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-1.5">
                {topDoors.map((door, index) =>
                  door ? (
                    <SuggestionCard
                      key={door.item_id}
                      data={door}
                      isSelected={door.item_id === currentSelectedDoor?.item_id}
                      onSelect={() => onSelectDoor(door)}
                      number={index + 1}
                    />
                  ) : (
                    <div
                      key={`placeholder-door-${index}`}
                      className="invisible"
                    >
                      <SuggestionCard
                        data={{
                          item_id: '',
                          name: '-',
                          domain: '-',
                          latitude: 0,
                          longitude: 0,
                          address: '-',
                          city: '',
                          state: '',
                          country: '',
                          zipcode: '',
                          chain_id: '',
                          chain_name: '',
                          chain_size: 0,
                          type: '',
                          score: 0,
                        }}
                        isSelected={false}
                        onSelect={() => {}}
                        number={index + 1}
                      />
                    </div>
                  )
                )}
                {/* Manual Search Button */}
                <ManualSearchCard
                  onClick={() => {
                    const hash = csvRow[InternalColumns.COMPANY_HASH]
                    setSelectedChain((prev) => {
                      const newState = { ...prev }
                      delete newState[hash]
                      return newState
                    })
                    setSelectedDoor((prev) => {
                      const newState = { ...prev }
                      delete newState[hash]
                      return newState
                    })
                    viewModel.setSelectedRowId(csvRow[InternalColumns.ROW_ID])
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ManualSearchCard({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="border rounded px-2 py-1 cursor-pointer transition-all border-dashed border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
      onClick={onClick}
    >
      <div className="flex items-center justify-center gap-2 py-1">
        <div className="text-gray-600 font-medium">Manual Search</div>
      </div>
    </div>
  )
}

function SuggestionCard({
  data,
  isSelected,
  onSelect,
  number,
}: {
  data: ChainSearchResult | DoorSearchResult
  isSelected: boolean
  onSelect: () => void
  number?: number
}) {
  const hasAddress = 'address' in data && data.address
  const isDoor = 'chain_id' in data
  const chainId = isDoor ? data.chain_id : `${data.type}${data.item_id}`
  const doorId = isDoor ? `${data.type}${data.item_id}` : null

  return (
    <div
      className={`border rounded px-2 py-1 cursor-pointer transition-all ${
        isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 bg-white hover:border-gray-400'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        {number && (
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-200 text-gray-700 rounded font-semibold">
            {number}
          </div>
        )}
        <div
          className={cn('text-green-600 font-semibold whitespace-nowrap', {
            invisible: !isSelected,
          })}
        >
          ✓
        </div>
        <div className="flex-1">
          <div
            className={`grid ${hasAddress ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}
          >
            <div className="font-semibold text-gray-900">
              {data.name || '-'}
            </div>
            <div className="text-gray-900">{data.domain || '-'}</div>
            {hasAddress && (
              <div className="text-gray-900">{data.address || '-'}</div>
            )}
          </div>
          <div className="mt-1 flex gap-2 text-xs text-gray-500">
            {doorId && <span>Door: {doorId}</span>}
            <span>Chain: {chainId}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function ComparisonSummary({
  inputData,
  selectedChain,
  selectedDoor,
  isChainManualMatch,
  isDoorManualMatch,
}: {
  inputData: { name: string; domain: string; address: string }
  selectedChain?: ChainSearchResult
  selectedDoor?: DoorSearchResult
  isChainManualMatch?: boolean
  isDoorManualMatch?: boolean
}) {
  const hasChain = !!selectedChain
  const hasDoor = !!selectedDoor

  return (
    <div>
      <div className="grid grid-cols-3 gap-4">
        {/* Input Column */}
        <div>
          <div className="text-sm font-semibold text-fb-dark-green mb-2 pb-1 border-b border-fb-dark-green">
            Your Input
          </div>
          <div className="space-y-2">
            <ComparisonField label="Name" value={inputData.name} />
            <ComparisonField label="Domain" value={inputData.domain} />
            <ComparisonField label="Address" value={inputData.address} />
          </div>
        </div>

        {/* Selected Chain Column */}
        <div>
          <div
            className={`text-sm font-semibold mb-2 pb-1 border-b flex items-center gap-2 ${hasChain ? 'text-green-700 border-green-300' : 'text-gray-400 border-gray-300'}`}
          >
            Selected Chain
            {isChainManualMatch && (
              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                Manual
              </span>
            )}
          </div>
          <div className="space-y-2">
            {hasChain ? (
              <>
                <ComparisonField
                  label="Name"
                  value={selectedChain.name || '-'}
                />
                <ComparisonField
                  label="Domain"
                  value={selectedChain.domain || '-'}
                />
                <ComparisonField label="Address" value="-" />
              </>
            ) : (
              <div className="text-sm text-gray-400 italic">
                No chain selected
              </div>
            )}
          </div>
        </div>

        {/* Selected Door Column */}
        <div>
          <div
            className={`text-sm font-semibold mb-2 pb-1 border-b flex items-center gap-2 ${hasDoor ? 'text-green-700 border-green-300' : 'text-gray-400 border-gray-300'}`}
          >
            Selected Door
            {isDoorManualMatch && (
              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                Manual
              </span>
            )}
          </div>
          <div className="space-y-2">
            {hasDoor ? (
              <>
                <ComparisonField
                  label="Name"
                  value={selectedDoor.name || '-'}
                />
                <ComparisonField
                  label="Domain"
                  value={selectedDoor.domain || '-'}
                />
                <ComparisonField
                  label="Address"
                  value={selectedDoor.address || '-'}
                />
              </>
            ) : (
              <div className="text-sm text-gray-400 italic">
                No door selected
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ComparisonField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-500">{label}</div>
        <div className="text-gray-900">{value}</div>
      </div>
    </div>
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="text-sm px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-gray-800">
      {children}
    </kbd>
  )
}
